/**
 * Utility functions for validating various ID formats and email addresses
 */

/**
 * Validates if a string is a valid email address
 * @param email - The email string to validate
 * @returns boolean indicating if the email is valid
 */
export const isValidEmail = (email: string | undefined | null): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return false;
  }
  
  // Basic email regex that requires domain extension
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmedEmail);
};

/**
 * Validates if a string is a valid Liquidity Request ID
 * @param id - The ID string to validate
 * @returns boolean indicating if the ID is valid
 */
export const isValidLRId = (id: string | undefined | null): boolean => {
  if (!id || typeof id !== 'string') {
    return false;
  }
  
  // LR IDs should start with LIQ- or ALT- and have reasonable length
  return (id.startsWith('LIQ-') || id.startsWith('ALT-')) && id.length > 8;
};

/**
 * Validates if a string is a valid account ID
 * @param accountId - The account ID string to validate
 * @returns boolean indicating if the account ID is valid
 */
export const isValidAccountId = (accountId: string | undefined | null): boolean => {
  if (!accountId || typeof accountId !== 'string') {
    return false;
  }
  
  // Account IDs should not be 'undefined' string and should have reasonable length
  return accountId !== 'undefined' && accountId.length > 3;
};

/**
 * Validates if a string is a valid asset ID
 * @param assetId - The asset ID string to validate
 * @returns boolean indicating if the asset ID is valid
 */
export const isValidAssetId = (assetId: string | undefined | null): boolean => {
  if (!assetId || typeof assetId !== 'string') {
    return false;
  }
  
  // Asset IDs typically start with INV- and have reasonable length
  return assetId.startsWith('INV-') && assetId.length > 8;
};
